/**
 * Strain library pages — index + per-strain detail.
 *
 * The strain library is a programmatic SEO surface: cannabis shoppers
 * routinely search "{strain} strain" / "{strain} near me" / "buy
 * {strain}". Each strain page renders FAQ + ItemList + breadcrumb schema
 * and lists every in-stock product matching the strain across the store.
 *
 * Backend endpoints:
 *   GET /api/strain-library/{store_slug}
 *   GET /api/strain-library/{store_slug}/{strain_slug}
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Leaf, Search, SlidersHorizontal, X } from "lucide-react";

import SEOHead from "../components/SEOHead";
import SchemaJsonLd from "../components/SchemaJsonLd";
import ProductCard from "../components/ProductCard";
import QuickAnswer from "../components/QuickAnswer";
import { breadcrumbSchema, faqSchema, speakableSchema, quickAnswerSchema } from "../lib/seo";
import MenuFilters from "./menu/MenuFilters";
import useFacetedFilters from "./menu/useFacetedFilters";

const readArrayParam = (sp, key) => {
    const raw = sp.get(key);
    return raw ? raw.split(",").filter(Boolean) : [];
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRAIN_TYPE_BADGE = {
    INDICA: { label: "Indica", className: "bg-purple-100 text-purple-900" },
    SATIVA: { label: "Sativa", className: "bg-amber-100 text-amber-900" },
    HYBRID: { label: "Hybrid", className: "bg-emerald-100 text-emerald-900" },
    INDICA_HYBRID: { label: "Indica-Hybrid", className: "bg-purple-100 text-purple-900" },
    SATIVA_HYBRID: { label: "Sativa-Hybrid", className: "bg-amber-100 text-amber-900" },
    HIGH_CBD: { label: "High CBD", className: "bg-blue-100 text-blue-900" },
};

export default function StrainLibrary() {
    const { slug = "queens-long-island" } = useParams();
    const [strains, setStrains] = useState(null);
    const [filter, setFilter] = useState("");
    const [type, setType] = useState("ALL");

    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/strain-library/${slug}`)
            .then((r) => r.json())
            .then((d) => {
                if (!cancelled) setStrains(d.strains || []);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const filtered = (strains || []).filter((s) => {
        if (type !== "ALL" && s.strain_type !== type) return false;
        const q = filter.trim().toLowerCase();
        return !q || s.name.toLowerCase().includes(q);
    });

    const canonical = `https://weeddispensarynearme.com/strains/${slug}`;
    const title = `Cannabis Strain Library — Indica · Sativa · Hybrid | Live NY Menu`;
    const desc = `Browse ${strains?.length || ""} cannabis strains in stock at ZenZest ${slug.replace(/-/g, " ")}. Blue Dream, Sour Diesel, Northern Lights, Gelato, Pineapple Express and more — sorted by availability, filterable by indica/sativa/hybrid.`;

    return (
        <>
            <SEOHead title={title} description={desc} canonical={canonical} />
            <SchemaJsonLd
                schema={[
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        { name: "Strains", url: canonical },
                    ]),
                    faqSchema([
                        {
                            q: "Which cannabis strains are in stock at ZenZest?",
                            a: `ZenZest stocks ${strains?.length || "200+"} unique cannabis strains across both flagships. The strain library is built live from inventory, so what you see is what's on shelf today.`,
                        },
                        {
                            q: "What's the difference between indica, sativa, and hybrid strains?",
                            a: "Indica strains are typically associated with body-heavy, relaxing effects and evening use. Sativas tend toward uplifting, energetic, daytime effects. Hybrids blend both. Personal response varies — filter by strain type and read the THC/CBD profiles on each product page.",
                        },
                        {
                            q: "Can I order a specific strain for delivery?",
                            a: "Yes. Find your strain in the library, click into it, and add eligible flower / pre-roll / vape / edible products to cart. Free same-day delivery on $100+ across 80+ NY metro ZIPs.",
                        },
                    ]),
                ]}
            />

            {/* HERO */}
            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 pt-24 pb-12 sm:pt-28">
                    <nav className="text-[11px] uppercase tracking-[0.25em] text-zen-cream-muted mb-6">
                        <Link to="/" className="hover:text-white">Home</Link>{" "}
                        ·{" "}
                        <Link to={`/menu/${slug}`} className="hover:text-white">
                            Live menu
                        </Link>{" "}
                        ·{" "}
                        <span className="text-zen-olive-light">Strain library</span>
                    </nav>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive-light mb-3 inline-flex items-center gap-3">
                        <span className="w-8 h-px bg-zen-olive-light" />
                        Cannabis strain library
                    </p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-4xl mb-5">
                        Every cannabis strain{" "}
                        <span className="italic text-zen-olive-light">live</span>{" "}
                        on shelf.
                    </h1>
                    <p className="text-white/80 text-base sm:text-lg max-w-2xl leading-relaxed">
                        {strains
                            ? `${strains.length} cannabis cultivars currently in stock — sorted by availability. `
                            : "Loading strain library… "}
                        Blue Dream, Sour Diesel, Northern Lights, Gelato, Pineapple Express,
                        OG Kush, GMO, Wedding Cake — and many more.
                    </p>
                </div>
            </section>

            {/* FILTERS */}
            <section className="sticky top-[72px] z-30 bg-zen-bg/90 backdrop-blur-xl border-b border-zen-line">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-3 flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zen-olive" />
                        <input
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Search strain (Blue Dream, Gelato…)"
                            data-testid="strain-search"
                            className="w-full bg-white border border-zen-ink/15 hover:border-zen-ink/30 focus:border-zen-olive text-zen-ink placeholder:text-zen-muted pl-9 pr-3 py-2.5 text-sm focus:outline-none transition-colors"
                        />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {["ALL", "INDICA", "SATIVA", "HYBRID", "HIGH_CBD"].map((t) => (
                            <button
                                key={t}
                                data-testid={`strain-filter-${t.toLowerCase()}`}
                                onClick={() => setType(t)}
                                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                    type === t
                                        ? "bg-zen-ink text-zen-cream border-zen-ink"
                                        : "border-zen-ink/15 text-zen-ink-soft hover:border-zen-ink/40"
                                }`}
                            >
                                {t === "ALL" ? "All" : STRAIN_TYPE_BADGE[t]?.label || t}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* GRID */}
            <section className="bg-zen-bg">
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-10">
                    {strains === null ? (
                        <p className="text-zen-ink-soft text-sm">Loading…</p>
                    ) : filtered.length === 0 ? (
                        <p className="text-zen-ink-soft text-sm">No matching strains.</p>
                    ) : (
                        <div
                            data-testid="strain-library-grid"
                            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5"
                        >
                            {filtered.map((s) => {
                                const badge = STRAIN_TYPE_BADGE[s.strain_type];
                                return (
                                    <Link
                                        key={s.slug}
                                        to={`/menu/${slug}/strain/${s.slug}`}
                                        data-testid={`strain-card-${s.slug}`}
                                        className="group bg-zen-surface border border-zen-line hover:border-zen-olive hover:-translate-y-0.5 transition-all p-4 flex flex-col gap-2 min-h-[112px]"
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            {badge ? (
                                                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${badge.className}`}>
                                                    {badge.label}
                                                </span>
                                            ) : (
                                                <Leaf size={12} className="text-zen-olive" />
                                            )}
                                            <span className="text-[10px] text-zen-muted tabular-nums">
                                                {s.product_count}
                                            </span>
                                        </div>
                                        <p className="font-serif text-lg text-zen-ink leading-tight">
                                            {s.name}
                                        </p>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}

export function StrainDetail() {
    const { storeSlug, strainSlug } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [library, setLibrary] = useState([]);
    const [error, setError] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // URL-backed filter state — share /strain/blue-dream?category=FLOWER&type=SATIVA links.
    const selectedCat = searchParams.get("category") || "ALL";
    const selectedStrains = readArrayParam(searchParams, "type");
    const selectedBrands = readArrayParam(searchParams, "brand");
    const selectedWeights = readArrayParam(searchParams, "weight");
    const selectedPotency = readArrayParam(searchParams, "potency");

    const setParam = (key, val) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (!val || val === "ALL") next.delete(key);
            else next.set(key, val);
            return next;
        });
    };
    const setSelectedCat = (v) => setParam("category", v === "ALL" ? null : v);
    const toggleArrParam = (key, prevVals) => (val) => {
        const next = prevVals.includes(val)
            ? prevVals.filter((v) => v !== val)
            : [...prevVals, val];
        setParam(key, next.length ? next.join(",") : null);
    };
    const toggleStrainType = toggleArrParam("type", selectedStrains);
    const toggleBrand = toggleArrParam("brand", selectedBrands);
    const toggleWeight = toggleArrParam("weight", selectedWeights);
    const togglePotency = toggleArrParam("potency", selectedPotency);
    const clearBrands = () => setParam("brand", null);
    const clearAllStrainFilters = () =>
        setSearchParams(new URLSearchParams(), { replace: true });

    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/strain-library/${storeSlug}/${strainSlug}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((d) => {
                if (cancelled) return;
                Promise.resolve().then(() => setData(d));
            })
            .catch(() => !cancelled && setError("not found"));
        // Library used for "Related strains" rail at page bottom.
        fetch(`${API}/strain-library/${storeSlug}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
                if (cancelled || !d) return;
                Promise.resolve().then(() => setLibrary(d.strains || []));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [storeSlug, strainSlug]);

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-4xl font-light mb-6 text-zen-ink">
                    Strain not found.
                </h1>
                <Link to={`/strains/${storeSlug || "queens-long-island"}`} className="text-zen-olive underline">
                    Browse the strain library
                </Link>
            </div>
        );
    }
    if (!data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-zen-ink-soft text-center">
                Loading {strainSlug}…
            </div>
        );
    }

    const canonical = `https://weeddispensarynearme.com/menu/${storeSlug}/strain/${data.slug}`;
    const badge = STRAIN_TYPE_BADGE[data.strain_type];
    const title = `${data.name} Strain — Buy Live Cannabis at ZenZest NY`;
    const desc = `${data.name} strain in stock at ZenZest. ${data.product_count} live products — flower, pre-rolls, vapes, edibles. Free same-day delivery on $100+ across 80+ NY metro ZIPs.`;

    return (
        <>
            <SEOHead title={title} description={desc} canonical={canonical} />
            <SchemaJsonLd
                schema={[
                    breadcrumbSchema([
                        { name: "Home", url: "https://weeddispensarynearme.com/" },
                        {
                            name: "Strain library",
                            url: `https://weeddispensarynearme.com/strains/${storeSlug}`,
                        },
                        { name: data.name, url: canonical },
                    ]),
                    faqSchema(
                        // Prefer LLM-generated FAQ entries (strain-specific) and
                        // fall back to the generic two-question shape.
                        (data.sections?.faq && data.sections.faq.length > 0
                            ? data.sections.faq
                            : [
                                  {
                                      q: `What is the ${data.name} strain?`,
                                      a: `${data.name} is a ${badge?.label || "cannabis"} cultivar carried at ZenZest. ${data.product_count} products in stock today across flower, pre-rolls, vapes, and/or edibles.`,
                                  },
                                  {
                                      q: `Can I get ${data.name} delivered to me?`,
                                      a: `Yes — drop your ZIP at /cannabis-delivery to confirm coverage. Free same-day delivery on $100+ across 80+ NY metro ZIPs.`,
                                  },
                              ]
                        ).map((f) => ({ q: f.q, a: f.a }))
                    ),
                    // AI Overview citation blocks — QAPage + Speakable.
                    ...(data.blurb
                        ? [
                              quickAnswerSchema({
                                  question: `What is the ${data.name} cannabis strain?`,
                                  answer: data.blurb,
                                  url: canonical,
                              }),
                          ]
                        : []),
                    speakableSchema(
                        ['[data-testid="strain-quick-answer-answer"]', '[data-testid="strain-blurb"]'],
                        canonical
                    ),
                    {
                        "@context": "https://schema.org",
                        "@type": "ItemList",
                        name: `${data.name} products at ZenZest`,
                        numberOfItems: data.product_count,
                        itemListElement: (data.products || []).slice(0, 20).map((p, i) => ({
                            "@type": "ListItem",
                            position: i + 1,
                            name: p.name,
                            url: `https://weeddispensarynearme.com/menu/${storeSlug}/${p.slug || ""}`,
                        })),
                    },
                ]}
            />

            {/* HERO */}
            <section className="bg-zen-elevated text-zen-cream">
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-24 pb-10 sm:pt-28">
                    <nav className="text-[11px] uppercase tracking-[0.25em] text-zen-cream-muted mb-6">
                        <Link to="/" className="hover:text-white">Home</Link>
                        {" · "}
                        <Link to={`/strains/${storeSlug}`} className="hover:text-white">
                            Strain library
                        </Link>
                        {" · "}
                        <span className="text-zen-olive-light">{data.name}</span>
                    </nav>
                    <div className="flex flex-wrap items-end justify-between gap-5">
                        <div>
                            {badge && (
                                <span className={`inline-block text-[10px] uppercase tracking-[0.25em] px-2 py-1 mb-4 ${badge.className}`}>
                                    {badge.label}
                                </span>
                            )}
                            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight">
                                {data.name}
                            </h1>
                            <p className="text-white/70 text-sm mt-3 uppercase tracking-[0.25em]">
                                {data.product_count} live products in stock
                            </p>
                        </div>
                        <Link
                            to={`/menu/${storeSlug}`}
                            className="border border-zen-cream/60 text-zen-cream px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-zen-cream hover:text-zen-ink transition-colors inline-flex items-center gap-2"
                        >
                            All strains <ArrowRight size={13} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* QuickAnswer — AI Overview citation block under the hero */}
            {data.blurb && (
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-6">
                    <QuickAnswer
                        question={`What is the ${data.name} cannabis strain?`}
                        answer={data.blurb}
                        testidPrefix="strain-quick-answer"
                    />
                </div>
            )}

            {/* PRODUCTS — sticky category chips + sidebar with faceted
                filters (Strain Type, Weight, THC Potency, Brand). Mobile
                gets a drawer button in the sticky toolbar. */}
            <StrainDetailProducts
                data={data}
                storeSlug={storeSlug}
                selectedCat={selectedCat}
                setSelectedCat={setSelectedCat}
                selectedStrains={selectedStrains}
                selectedBrands={selectedBrands}
                selectedWeights={selectedWeights}
                selectedPotency={selectedPotency}
                onToggleStrain={toggleStrainType}
                onToggleBrand={toggleBrand}
                onToggleWeight={toggleWeight}
                onTogglePotency={togglePotency}
                onClearBrands={clearBrands}
                mobileFiltersOpen={mobileFiltersOpen}
                setMobileFiltersOpen={setMobileFiltersOpen}
                onClearAll={clearAllStrainFilters}
            />

            {/* EDITORIAL — moved AFTER products. Two-column layout on desktop:
                left rail is the headline blurb + key-fact tiles; right rail is
                the long-form facet sections. FAQ spans full-width below. */}
            {(data.sections || data.blurb) && (
                <section className="bg-zen-surface border-b border-zen-line">
                    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                            About {data.name}
                        </p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-zen-ink leading-[1.05] mb-10 max-w-3xl">
                            Everything to know about{" "}
                            <span className="italic text-zen-olive">{data.name}.</span>
                        </h2>
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                            {/* LEFT — blurb + at-a-glance tiles */}
                            <aside className="lg:col-span-4 space-y-6">
                                {data.blurb && (
                                    <div data-testid="strain-blurb">
                                        <p className="font-serif text-lg leading-[1.6] text-zen-ink/90 italic border-l-2 border-zen-olive pl-5">
                                            {data.blurb}
                                        </p>
                                    </div>
                                )}
                                <dl className="bg-zen-bg border border-zen-line divide-y divide-zen-line">
                                    {badge && (
                                        <div className="flex justify-between gap-3 px-4 py-3 text-sm">
                                            <dt className="text-zen-ink-soft uppercase tracking-[0.18em] text-[10px]">
                                                Type
                                            </dt>
                                            <dd className="text-zen-ink font-medium">{badge.label}</dd>
                                        </div>
                                    )}
                                    <div className="flex justify-between gap-3 px-4 py-3 text-sm">
                                        <dt className="text-zen-ink-soft uppercase tracking-[0.18em] text-[10px]">
                                            In stock
                                        </dt>
                                        <dd className="text-zen-ink font-medium tabular-nums">
                                            {data.product_count} products
                                        </dd>
                                    </div>
                                </dl>
                            </aside>
                            {/* RIGHT — long-form facet sections.
                                Prefer v2 deep sections; fall back to v1 if older cache. */}
                            {data.sections && (
                                <div
                                    data-testid="strain-sections"
                                    className="lg:col-span-8 space-y-7"
                                >
                                    {[
                                        ["lineage_deep", "lineage", "Lineage & origin"],
                                        ["effects_deep", "effects", "Effects"],
                                        ["aroma_flavor_deep", "aroma_flavor", "Aroma, flavor & terpenes"],
                                        ["appearance_deep", "appearance", "Appearance"],
                                        ["growing_notes", null, "Growing notes"],
                                        ["ideal_moment_deep", "ideal_moment", "When it's best enjoyed"],
                                    ]
                                        .map(([deepKey, fallbackKey, label]) => {
                                            const value =
                                                data.sections[deepKey] ||
                                                (fallbackKey ? data.sections[fallbackKey] : null);
                                            return value ? [deepKey, label, value] : null;
                                        })
                                        .filter(Boolean)
                                        .map(([k, label, value]) => (
                                            <div
                                                key={k}
                                                data-testid={`strain-section-${k}`}
                                                className="bg-zen-bg border border-zen-line p-5 sm:p-6"
                                            >
                                                <h3 className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                                    {label}
                                                </h3>
                                                <p className="text-base sm:text-[17px] leading-[1.75] text-zen-ink/90 whitespace-pre-line">
                                                    {value}
                                                </p>
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* SIMILAR STRAINS (v2 only) — internal-link copy
                            mid-page. Drives crawl depth + recirculation
                            without needing the data from /strain-library. */}
                        {Array.isArray(data.sections?.similar_strains) &&
                            data.sections.similar_strains.length > 0 && (
                                <div
                                    data-testid="strain-similar-editorial"
                                    className="mt-12 max-w-4xl mx-auto"
                                >
                                    <h3 className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-5 text-center">
                                        If you like {data.name}, also explore
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {data.sections.similar_strains.map((s, i) => {
                                            const targetSlug = (s.name || "")
                                                .toLowerCase()
                                                .normalize("NFKD")
                                                .replace(/[^a-z0-9]+/g, "-")
                                                .replace(/^-+|-+$/g, "");
                                            const inLibrary = (library || []).some(
                                                (x) => x.slug === targetSlug
                                            );
                                            const cardCls =
                                                "bg-zen-bg border border-zen-line p-4 hover:border-zen-olive transition";
                                            const inner = (
                                                <>
                                                    <p className="font-serif text-lg text-zen-ink leading-snug">
                                                        {s.name}
                                                    </p>
                                                    {s.why && (
                                                        <p className="text-[13px] text-zen-ink-soft mt-1 leading-relaxed">
                                                            {s.why}
                                                        </p>
                                                    )}
                                                </>
                                            );
                                            return inLibrary ? (
                                                <Link
                                                    key={i}
                                                    to={`/menu/${storeSlug}/strain/${targetSlug}`}
                                                    data-testid={`strain-similar-editorial-${i}`}
                                                    className={`${cardCls} block group`}
                                                >
                                                    {inner}
                                                </Link>
                                            ) : (
                                                <div
                                                    key={i}
                                                    data-testid={`strain-similar-editorial-${i}`}
                                                    className={cardCls}
                                                >
                                                    {inner}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        {data.sections?.faq?.length > 0 && (
                            <div data-testid="strain-section-faq" className="mt-12 max-w-3xl mx-auto">
                                <h3 className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-5 text-center">
                                    Common questions
                                </h3>
                                <div className="space-y-3 bg-zen-bg border border-zen-line">
                                    {data.sections.faq.map((f, i) => (
                                        <details
                                            key={i}
                                            data-testid={`strain-faq-${i}`}
                                            className="group border-b border-zen-line last:border-b-0 [&_summary::-webkit-details-marker]:hidden"
                                        >
                                            <summary className="cursor-pointer flex items-start justify-between gap-4 text-base font-serif text-zen-ink leading-snug px-5 py-4">
                                                <span>{f.q}</span>
                                                <span className="text-zen-olive shrink-0 transition-transform group-open:rotate-45 text-xl leading-none">
                                                    +
                                                </span>
                                            </summary>
                                            <p className="px-5 pb-5 text-sm text-zen-ink-soft leading-relaxed">
                                                {f.a}
                                            </p>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* RELATED STRAINS — internal-link rail, same strain_type, 6 max */}
            {(() => {
                const related = (library || [])
                    .filter(
                        (s) =>
                            s.slug !== data.slug &&
                            s.strain_type &&
                            data.strain_type &&
                            // Same broad family: INDICA/INDICA_HYBRID/HYBRID/SATIVA_HYBRID/SATIVA share root.
                            s.strain_type === data.strain_type
                    )
                    .slice(0, 6);
                if (related.length === 0) return null;
                return (
                    <section
                        data-testid="strain-related"
                        className="bg-zen-bg"
                    >
                        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                                Related strains
                            </p>
                            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-zen-ink leading-tight mb-7 max-w-2xl">
                                If you like {data.name}, also try…
                            </h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
                                {related.map((s) => {
                                    const sBadge = STRAIN_TYPE_BADGE[s.strain_type];
                                    return (
                                        <Link
                                            key={s.slug}
                                            to={`/menu/${storeSlug}/strain/${s.slug}`}
                                            data-testid={`strain-related-${s.slug}`}
                                            className="group bg-zen-surface border border-zen-line hover:border-zen-olive hover:-translate-y-0.5 transition-all p-4 flex flex-col gap-2 min-h-[112px]"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                {sBadge && (
                                                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${sBadge.className}`}>
                                                        {sBadge.label}
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-zen-muted tabular-nums">
                                                    {s.product_count}
                                                </span>
                                            </div>
                                            <p className="font-serif text-lg text-zen-ink leading-tight">
                                                {s.name}
                                            </p>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                );
            })()}
        </>
    );
}


/**
 * Products section for the StrainDetail page. Renders the sticky category
 * chip row, the desktop sidebar with faceted filters, the mobile-filters
 * drawer (with its own Filters toggle in the sticky toolbar), and the
 * 5-per-row product grid.
 */
function StrainDetailProducts({
    data,
    storeSlug,
    selectedCat,
    setSelectedCat,
    selectedStrains,
    selectedBrands,
    selectedWeights,
    selectedPotency,
    onToggleStrain,
    onToggleBrand,
    onToggleWeight,
    onTogglePotency,
    onClearBrands,
    mobileFiltersOpen,
    setMobileFiltersOpen,
    onClearAll,
}) {
    const cats = useMemo(
        () =>
            Array.from(
                new Set((data.products || []).map((p) => p.category).filter(Boolean))
            ).sort(),
        [data.products]
    );

    const poolForFacets = useMemo(() => {
        if (selectedCat === "ALL") return data.products || [];
        return (data.products || []).filter((p) => p.category === selectedCat);
    }, [data.products, selectedCat]);

    const {
        applyFilters,
        strainOptions,
        weightOptions,
        potencyOptions,
        brandOptions,
    } = useFacetedFilters(poolForFacets, {
        selectedStrains,
        selectedBrands,
        selectedWeights,
        selectedPotency,
    });

    const filteredProducts = useMemo(
        () => applyFilters(poolForFacets),
        [applyFilters, poolForFacets]
    );

    const sidebarActiveCount =
        selectedStrains.length +
        selectedBrands.length +
        selectedWeights.length +
        selectedPotency.length;
    const totalActiveCount = sidebarActiveCount + (selectedCat !== "ALL" ? 1 : 0);

    const filterPanelProps = {
        selectedStrains,
        selectedBrands,
        selectedWeights,
        selectedPotency,
        strainOptions,
        weightOptions,
        potencyOptions,
        brandOptions,
        onToggleStrain,
        onToggleBrand,
        onToggleWeight,
        onTogglePotency,
        onClearBrands,
    };

    return (
        <>
            {cats.length > 1 && (
                <section className="sticky top-[72px] z-30 bg-zen-bg/90 backdrop-blur-xl border-b border-zen-ink/10">
                    <div className="max-w-[1400px] mx-auto px-3 sm:px-5 py-3 flex items-center gap-3">
                        <button
                            onClick={() => setMobileFiltersOpen(true)}
                            data-testid="strain-mobile-filters-toggle"
                            className="lg:hidden shrink-0 inline-flex items-center gap-2 px-3 py-2 border border-zen-ink/15 text-zen-ink text-[11px] uppercase tracking-[0.22em] hover:border-zen-ink/40"
                        >
                            <SlidersHorizontal size={14} />
                            Filters
                            {sidebarActiveCount > 0 && (
                                <span className="bg-zen-olive text-zen-bg text-[10px] w-5 h-5 inline-flex items-center justify-center rounded-full">
                                    {sidebarActiveCount}
                                </span>
                            )}
                        </button>
                        <div
                            data-testid="strain-category-chips"
                            className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0"
                        >
                            <button
                                onClick={() => setSelectedCat("ALL")}
                                data-testid="strain-cat-all"
                                className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                    selectedCat === "ALL"
                                        ? "bg-zen-ink text-zen-cream border-zen-ink"
                                        : "border-zen-line text-zen-ink-soft hover:border-zen-ink/40"
                                }`}
                            >
                                All ({data.products?.length || 0})
                            </button>
                            {cats.map((c) => {
                                const count = (data.products || []).filter((p) => p.category === c).length;
                                return (
                                    <button
                                        key={c}
                                        onClick={() => setSelectedCat(c)}
                                        data-testid={`strain-cat-${c.toLowerCase()}`}
                                        className={`shrink-0 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                            selectedCat === c
                                                ? "bg-zen-ink text-zen-cream border-zen-ink"
                                                : "border-zen-line text-zen-ink-soft hover:border-zen-ink/40"
                                        }`}
                                    >
                                        {c.replace(/_/g, " ")} ({count})
                                    </button>
                                );
                            })}
                        </div>
                        {totalActiveCount > 0 && (
                            <button
                                onClick={onClearAll}
                                data-testid="strain-clear-filters"
                                className="shrink-0 text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink hidden sm:inline-flex items-center gap-1"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </section>
            )}

            <section className="bg-zen-bg border-b border-zen-line">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-5 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
                        <aside className="hidden lg:block">
                            <MenuFilters {...filterPanelProps} />
                        </aside>

                        {mobileFiltersOpen && (
                            <div
                                data-testid="strain-mobile-filters-drawer"
                                className="fixed inset-0 z-[130] bg-zen-ink/40 backdrop-blur-sm lg:hidden"
                                onClick={() => setMobileFiltersOpen(false)}
                            >
                                <aside
                                    onClick={(e) => e.stopPropagation()}
                                    className="absolute top-0 left-0 bottom-0 w-[85%] max-w-sm bg-zen-bg p-6 overflow-y-auto"
                                >
                                    <div className="flex items-center justify-between mb-6">
                                        <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive">
                                            Filters
                                        </p>
                                        <button
                                            onClick={() => setMobileFiltersOpen(false)}
                                            aria-label="Close filters"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                    <MenuFilters {...filterPanelProps} />
                                    {totalActiveCount > 0 && (
                                        <button
                                            onClick={() => {
                                                onClearAll();
                                                setMobileFiltersOpen(false);
                                            }}
                                            data-testid="strain-mobile-clear-filters"
                                            className="mt-6 w-full py-2.5 border border-zen-ink/20 text-[11px] uppercase tracking-[0.22em] text-zen-ink hover:bg-zen-surface"
                                        >
                                            Clear filters
                                        </button>
                                    )}
                                </aside>
                            </div>
                        )}

                        <div>
                            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5 px-3 sm:px-0">
                                <h2 className="font-serif text-2xl sm:text-3xl font-light text-zen-ink leading-tight">
                                    Shop {data.name}{" "}
                                    <span className="text-zen-ink-soft text-base font-normal">
                                        · {filteredProducts.length} of {data.product_count} live products
                                    </span>
                                </h2>
                            </div>
                            {filteredProducts.length ? (
                                <div
                                    data-testid="strain-detail-grid"
                                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-2.5"
                                >
                                    {filteredProducts.map((p) => (
                                        <ProductCard
                                            key={p.id}
                                            product={p}
                                            storeSlug={storeSlug}
                                            specialsById={{}}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 border border-zen-ink/10 bg-zen-surface">
                                    <p className="font-serif text-2xl text-zen-ink font-light mb-3">
                                        No products match these filters right now.
                                    </p>
                                    {totalActiveCount > 0 && (
                                        <button
                                            onClick={onClearAll}
                                            data-testid="strain-reset-filters"
                                            className="text-[11px] uppercase tracking-[0.25em] text-zen-olive border-b border-zen-olive/40 pb-1"
                                        >
                                            Reset filters
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
